import { useGetProductsByCategory } from '@/hooks/useProductHook'
import type { Product } from '@/types'
import { type Category } from '@/types'
import { useRouter } from 'next/router'
import type { ParsedUrlQuery } from 'querystring'
import { useEffect, useState } from 'react'

const useCategoryProducts = (category: Category) => {
  const { rowOfProductsOnMobile = 10, productsPerRowOnMobile = 4 } =
    category || {}
  const limit = rowOfProductsOnMobile * productsPerRowOnMobile

  const [data, setData] = useState<{
    page: number
    totalPages: number
    totalProducts: number
    limit: number
    products: Product[]
  }>({
    page: 0,
    totalPages: 0,
    totalProducts: 0,
    limit,
    products: [],
  })

  const router = useRouter()
  const { getProducts } = useGetProductsByCategory()
  const [loading, setLoading] = useState(false)
  const { page, totalPages, products } = data

  const getData = (page: number, query: ParsedUrlQuery) => {
    setLoading(true)
    setData({ ...data, page })
    getProducts(
      {
        limit,
        categoryDefault: category._id,
        currentPage: page,
        sortBy: 'catOrder',
        sortType: 'asc',
        catDisplay: true,
        ...query,
      },
      {
        onSuccess: res =>
          setData({
            ...data,
            page: page,
            totalPages: res.data?.pages || 0,
            totalProducts: res.data?.total || 0,
            products:
              page === 0
                ? [...(res.data?.data || [])]
                : [...products, ...(res.data?.data || [])],
          }),
        onSettled: () => setLoading(false),
      }
    )
  }

  useEffect(() => {
    getData(page, router.query)
  }, [page])

  useEffect(() => {
    getData(0, router.query)
  }, [router.query])

  const loadingMore = page !== 0 && loading
  const canLoadMore = page < totalPages - 1

  const loadMore = () => canLoadMore && setData({ ...data, page: page + 1 })

  return {
    ...data,
    loading,
    loadingMore,
    canLoadMore,
    loadMore,
  }
}

export default useCategoryProducts
